import { defineStore } from 'pinia'
import { ref } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { captureApiException } from '@/utility/sentry'

const bitrixApi = import.meta.env.VITE_BITRIXAPI

export const useNotificationStore = defineStore('notifications', () => {

    const messages = ref([])
    const showSidebar = ref(false)
    const total = ref(0)
    const page = ref(1)
    const loading = ref(false)
    const unreadCount = ref(0)

    let controller = null
    let deleteController = null
    let fetchInFlight = null
    let fetchInFlightPage = null

    async function fetchNotifications(pageNum = 1) {
        // Reuse identical in-flight request to avoid startup double-fetch races.
        if (fetchInFlight && fetchInFlightPage === pageNum)
            return fetchInFlight

        if (controller) controller.abort()
        controller = new AbortController()
        loading.value = true
        page.value = pageNum

        const token = localStorage.getItem('id_token_portalbitrix')
        
        const request = (async () => {
            try {
                const response = await axios.get(bitrixApi + 'partner/messagesV2?notification=true&page=' + pageNum, {
                    headers: {
                        Authorization: 'Bearer ' + token,
                        'x-localization': 'da'
                    },
                    signal: controller.signal
                })

                const res = response.data.data
                const filteredMsg = []

                res.items.forEach(item => {
                    if (item['ACTIONS'] === null || item['ACTIONS'].IS_DONE === false) {
                        filteredMsg.push(item)
                    }
                })

                messages.value = filteredMsg
                total.value = res.total
                if (res.unread !== undefined) {
                    unreadCount.value = res.unread
                }
                loading.value = false

                return res
            } catch (error) {
                loading.value = false
                if (!axios.isCancel(error)) {
                    ElMessage.error(error?.response?.data?.message || 'Der opstod en fejl')
                    captureApiException(error, 'notifications.js', 'fetchNotifications')
                    throw error
                }
                return null
            } finally {
                if (fetchInFlight === request) {
                    fetchInFlight = null
                    fetchInFlightPage = null
                }
            }
        })()

        fetchInFlight = request
        fetchInFlightPage = pageNum

        return request
    }

    async function deleteNotification(id, index) {
        if (deleteController) deleteController.abort()
        deleteController = new AbortController()

        messages.value.splice(index, 1)

        const token = localStorage.getItem('id_token_portalbitrix')

        try {
            await axios.post(bitrixApi + 'partner/message/delete', 
                { messageId: id },
                {
                    headers: {
                        Authorization: 'Bearer ' + token,
                        'x-localization': 'da'
                    },
                    signal: deleteController.signal
                }
            )
        } catch (error) {
            if (!axios.isCancel(error)) {
                ElMessage.error(error?.response?.data?.message || 'Der opstod en fejl')
                captureApiException(error, 'notifications.js', 'deleteNotification')
                throw error
            }
        }
    }

    function cancelFetch() {
        if (controller) {
            controller.abort()
            controller = null
        }
        if (deleteController) {
            deleteController.abort()
            deleteController = null
        }
    }

    const resetNotifications = () => {
        messages.value = []
        showSidebar.value = false
        total.value = 0
        page.value = 1
        loading.value = false
        unreadCount.value = 0
    }

    return {
        messages,
        showSidebar,
        total,
        page,
        loading,
        unreadCount,
        fetchNotifications,
        deleteNotification,
        resetNotifications,
        cancelFetch
    }
})
